'use client';

import { createContext, useContext, useEffect, useState } from 'react';

export type InputMethod = 'mouse' | 'touch' | 'unknown';

interface Props {
  lastInputMethod: InputMethod;
}

const LastInputMethodContext = createContext<Props | undefined>(undefined);

export const LastInputMethodProvider = ({
  children,
}: React.PropsWithChildren) => {
  const [input, setInput] = useState<InputMethod>('unknown');

  useEffect(() => {
    // Pointer Events cover mouse/touch/pen
    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType === 'mouse') setInput('mouse');
      else if (e.pointerType === 'touch' || e.pointerType === 'pen')
        setInput('touch');
    };

    const onMouseMove = () =>
      setInput((prev) => (prev === 'touch' ? prev : 'mouse'));
    const onTouchStart = () => setInput('touch');

    window.addEventListener('pointerdown', onPointerDown, { passive: true });
    window.addEventListener('mousemove', onMouseMove, { passive: true });
    window.addEventListener('touchstart', onTouchStart, { passive: true });

    return () => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('touchstart', onTouchStart);
    };
  }, []);

  return (
    <LastInputMethodContext.Provider value={{ lastInputMethod: input }}>
      {children}
    </LastInputMethodContext.Provider>
  );
};

export const useLastInputMethod = () => useContext(LastInputMethodContext)!;
